<template>
  <div class="pagination-wrapper">
    <span class="total-text">共{{ total }}条</span>
    <el-select
      v-model="currentPageSize"
      :size="size"
      class="size-select"
      @change="handleSizeChange"
    >
      <el-option
        v-for="item in pageSizes"
        :key="item"
        :label="`${item}条/页`"
        :value="item"
      />
    </el-select>
    <el-pagination
      v-model:current-page="currentPage"
      :page-size="currentPageSize"
      :total="total"
      layout="prev, pager, next"
      :pager-count="5"
      :size="size"
      background
      @current-change="handleCurrentChange"
    />
    <div class="jump-wrapper">
      跳至
      <el-input-number
        v-model="jumpPage"
        :min="1"
        :max="pageCount"
        :controls="false"
        :size="size"
        class="jump-input"
        @keyup.enter="handleJump"
      />
      页
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

const props = withDefaults(
  defineProps<{
    total: number;
    modelValue?: number;
    pageSize?: number;
    pageSizes?: number[];
    size?: "large" | "default" | "small";
  }>(),
  {
    modelValue: 1,
    pageSize: 10,
    pageSizes: () => [10, 20, 50, 100],
    size: "default",
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: number): void;
  (e: "update:pageSize", value: number): void;
  (e: "change", page: number, pageSize: number): void;
}>();

/** 当前页码 */
const currentPage = computed({
  get: () => props.modelValue,
  set: (val) => emit("update:modelValue", val),
});

/** 每页条数 */
const currentPageSize = computed({
  get: () => props.pageSize,
  set: (val) => emit("update:pageSize", val),
});

/** 总页数 */
const pageCount = computed(() => Math.ceil(props.total / props.pageSize) || 1);

/** 跳转输入框值 */
const jumpPage = ref(props.modelValue);

/** 同步跳转框 */
watch(
  () => props.modelValue,
  (val) => {
    jumpPage.value = val;
  }
);

function handleSizeChange(val: number) {
  emit("update:pageSize", val);
  // 切换每页条数时重置到第1页
  if (props.modelValue !== 1) {
    emit("update:modelValue", 1);
  }
  emit("change", 1, val);
}

function handleCurrentChange(page: number) {
  emit("change", page, props.pageSize);
}

function handleJump() {
  const page = Math.min(Math.max(1, Number(jumpPage.value)), pageCount.value);
  jumpPage.value = page;
  if (page !== props.modelValue) {
    emit("update:modelValue", page);
    emit("change", page, props.pageSize);
  }
}
</script>

<style lang="scss" scoped>
.pagination-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;

  .total-text {
    font-size: var(--el-font-size-base);
    color: var(--el-text-color-regular);
    white-space: nowrap;
  }

  .size-select {
    width: 110px;
  }

  :deep(.el-pagination) {
    --el-pagination-button-bg-color: #fff;

    li {
      &.is-active {
        background-color: var(--el-color-primary);
        border-radius: var(--el-border-radius-small);
      }

      &:hover:not(.is-active) {
        color: var(--el-color-primary);
      }
    }
  }

  .jump-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: var(--el-font-size-base);
    color: var(--el-text-color-regular);
    white-space: nowrap;

    .jump-input {
      width: 56px;

      :deep(.el-input__inner) {
        text-align: center;
        padding-left: 8px;
        padding-right: 8px;
      }
    }
  }
}
</style>
